import { CanvasView } from '../../client/canvas-view';
import type { ClientHost } from '../../client/mode';
import type { Pointer, PlatformSocket } from '../../client/platform';
import { requestId } from '../../client/platform';
import { C, T, type IconName } from '../../client/paint';
import { brushWidthForRatio } from '../../shared/brush';
import { renderDrawingTools, renderBrushCatalog, drawingToolLayout, brushCatalogBox, selectDrawingBrush, snappedLine, renderDragPreview } from '../../client/drawing-tools';
import { DrawingEditor, renderDrawing, DRAG_BRUSHES, STROKE_DEFAULT_WIDTH, SHAPE_FILL_OPACITY } from '../../shared/art/drawing';
import type { Point, Stroke, StrokeBrush, StrokeShape } from '../../shared/art/types';
import { LIVE_DURATIONS, LIVE_FEEDBACK, LIVE_PROTOCOL_VERSION, LIVE_CAPACITY, LIVE_REACTIONS, LIVE_REACTION_COOLDOWN, type LiveGuessCommand, type LiveReactionKind, type LiveReaction, type LiveMember, type LiveAnswerMode, type LiveRoomConfig, type LiveRoomView, type LiveRoomSummary, type LiveServerMessage, type LiveClientMessage, type LiveDrawingOperation, type LiveDrawingCommand } from './types';
import { countdownEdges, reactionIcon, reactionFlight } from './client-effects';
import { LIVE_ROOM_NAMES } from './content';
import { LiveCanvasModel, liveBoardLayout } from './client-model';

const ANSWER_LABELS: Record<LiveAnswerMode, string> = { candidates: '选字', text: '自由输入', both: '选字或自由输入' };
const FEEDBACK = LIVE_FEEDBACK;
const ROOT = '/modes/live-draw';
type Panel = 'brush' | 'ranking' | 'members' | 'feedback' | 'settlement' | 'bubble' | null;
type PendingDrawing = { id: string; command: LiveDrawingCommand; sent: number };

export class LiveClient {
  room: LiveRoomView | null = null;
  rooms: LiveRoomSummary[] = [];
  config: LiveRoomConfig = { name: LIVE_ROOM_NAMES[0], duration: 120, answerMode: 'text', isPublic: true };
  private editingRoomId: string | null = null;
  model = new LiveCanvasModel();
  board = liveBoardLayout(740);
  connected = false;
  connecting = false;
  loading = false;
  panel: Panel = null;
  brush: StrokeBrush = 'pen';
  widths = { ...STROKE_DEFAULT_WIDTH };
  color = '#000000';
  eraser = false;
  eraserWidth = 26;
  shape: StrokeShape = 'circle';
  shapeFilled = false;
  answer = '';
  selected: number[] = [];
  keyboardHeight = 0;
  private keyboardPixels = 0;
  keyboardOpen = false;
  lobbyPage = 0;
  private socket?: PlatformSocket;
  private epoch = 0;
  private reconnectAt = 0;
  private lastPing = 0;
  private lastReceived = 0;
  private lastLobby = 0;
  private lastSend = 0;
  private clockOffset = 0;
  private seq = 0;
  private receivedSeq = 0;
  private pending: PendingDrawing[] = [];
  private pointerId: number | null = null;
  private slidingId: number | null = null;
  private consumed = new Set<number>();
  private points: Point[] = [];
  private active?: Stroke;
  private draft?: DrawingEditor;
  private queuedGuess = new Set<string>();
  private pendingGuess?: { id: string; command: LiveGuessCommand; sent: number; attempts: number };
  private ownGuessText = '';
  private lastFeedback = 0;
  private reactionTarget = '';
  private lastReaction = -Infinity;
  private reactions: (LiveReaction & { receivedAt: number })[] = [];
  private seenReactions = new Map<string, number>();
  private bubbleText = '';
  private panelPage = 0;
  private seenSettlement = '';
  private accelerationUntil = 0;
  private seatPositions = new Map<string, { x: number; y: number }>();
  private feedbackText = '';
  private feedbackUntil = 0;
  private buffer: HTMLCanvasElement;
  private bufferRevision = -1;
  get usedPalette() { return this.host.paletteSelection.id; }
  set usedPalette(id: string) { this.host.paletteSelection.id = id; }
  get width() { return this.widths[this.brush]; }
  set width(value: number) { this.widths[this.brush] = value; }
  get brushWidths() { return this.widths; }
  get brushPanel() { return this.panel === 'brush'; }
  set brushPanel(open: boolean) { this.panel = open ? 'brush' : null; }
  readonly view = new CanvasView();
  private readonly defaultView = new CanvasView();
  get zoom() { return this.view.zoom; }
  set zoom(value: number) { this.view.zoom = value; }
  get zoomPan() { return this.view.pan; }
  set zoomPan(value: { x: number; y: number }) { this.view.pan = value; }
  private lineOptions?: import('../../shared/art/drawing').StrokeOptions;
  private lineFrom?: { x: number; y: number };
  private lineTo?: { x: number; y: number };
  private pendingInk?: Stroke;
  private selectionRefreshes = 0;
  private snapshotRequested = false;
  private avatars = new Map<string, HTMLImageElement>();
  private avatarRequests = new Set<string>();
  private avatarRetryAt = new Map<string, number>();
  private avatarGeneration = 0;
  private pickedAt = 0;
  private answerFeedback = '';
  private answerFeedbackUntil = 0;
  constructor(readonly host: ClientHost) { this.buffer = host.platform.createCanvas(1000, 1000); }
  private async task(action: () => Promise<void>) {
    try { await action(); }
    catch (error) { this.host.toast(error instanceof Error ? error.message : '连接暂时不可用，请重试。'); }
  }
  private path(suffix = '') { return ROOT + '/rooms/' + this.room!.id + suffix; }
  private me() { return this.room?.members.find(member => member.id === this.host.player?.id); }
  private ownsRoom() { return this.room?.ownerId === this.host.player?.id; }
  private canDraw() { return this.host.screen === 'live-room' && this.connected && this.room?.role === 'artist' && this.room.phase === 'drawing' && Date.now() + this.clockOffset < this.room.round!.deadlineAt; }
  private canGuess() { return this.connected && this.room?.role === 'guesser' && this.room.phase === 'drawing' && !this.me()?.guessed; }
  private button(id: string, label: string, x: number, y: number, w: number, h: number, action: () => void, selected = false, disabled = false, icon?: IconName) {
    this.host.paint.button('live-' + id, label, this.host.paint.x(x), y, this.host.paint.span(w), h, action, selected ? 'primary' : 'plain', disabled, icon);
  }
  private icon(id: string, label: string, icon: IconName, x: number, y: number, action: () => void, disabled = false, selected = false) {
    this.host.paint.iconButton('live-' + id, label, icon, this.host.paint.x(x), y, 18, action, selected ? 'orange' : 'secondary', disabled);
  }
  createScreen() {
    this.editingRoomId = null;
    this.config = { name: LIVE_ROOM_NAMES[Math.floor(Math.random() * LIVE_ROOM_NAMES.length)], duration: 120, answerMode: 'text', isPublic: true };
    this.host.go('live-create');
  }
  private settingsScreen() {
    const room = this.room;
    if (!room || !this.ownsRoom() || room.phase !== 'waiting' || room.match) return;
    this.editingRoomId = room.id; this.panel = null;
    this.config = { name: room.name, duration: room.duration, answerMode: room.answerMode, isPublic: room.isPublic };
    this.host.go('live-create');
  }
  private async saveSettings() {
    const roomId = this.editingRoomId;
    if (!roomId || this.loading) return;
    this.loading = true;
    try {
      const next = await this.host.api<LiveRoomView>(ROOT + '/rooms/' + roomId, 'PATCH', { ...this.config });
      if (this.room?.id !== roomId) return;
      this.receiveState(next);
      if (this.editingRoomId === roomId) { this.editingRoomId = null; this.host.go('live-room', -1); }
      this.host.toast('房间设置已保存。');
    } finally { this.loading = false; }
  }
  lobby(dir: 1 | -1 = 1) {
    this.closeKeyboard(); this.host.go('lobby', dir); this.lastLobby = 0;
    void this.task(() => this.refreshLobby());
  }
  async refreshLobby() {
    if (this.loading) return; this.loading = true; this.lastLobby = Date.now();
    try {
      const result = await this.host.api<{ rooms: LiveRoomSummary[] }>(ROOT + '/rooms'); this.rooms = result.rooms;
      this.lobbyPage = Math.min(this.lobbyPage, Math.max(0, Math.ceil(this.rooms.length / this.lobbyPageSize()) - 1));
    } finally { this.loading = false; }
  }
  async recover() {
    const result = await this.host.api<{ room: LiveRoomView | null }>(ROOT + '/current');
    if (result.room) this.open(result.room, false);
  }
  async join(code: string) {
    const match = code.match(/(?:[?&]|^)room=([^&]+)/); const clean = decodeURIComponent(match?.[1] ?? code).trim();
    if (!clean) return;
    if (this.room && this.room.id !== clean && this.room.code !== clean.toUpperCase()) {
      if (!await this.host.platform.confirm('加入新的房间？', '将离开当前派对，并释放当前席位。')) return;
      await this.leave(false);
    }
    this.open(await this.host.api<LiveRoomView>(ROOT + '/join', 'POST', { code: clean }));
  }
  async promptJoin() { const code = await this.host.platform.prompt('加入画猜派对', '输入房间码'); if (code) await this.join(code); }
  open(room: LiveRoomView, navigate = true) {
    const changed = this.room?.id !== room.id;
    if (changed) { this.disconnect(); this.pending = []; this.seq = 0; this.receivedSeq = 0; }
    this.receiveState(room); if (navigate) this.host.go('live-room'); this.connect();
  }
  private async create() {
    if (this.loading) return;
    if (this.room) {
      if (!await this.host.platform.confirm('创建新的派对？', '将离开当前房间。')) return;
      await this.leave(false);
    }
    this.loading = true;
    try { this.open(await this.host.api<LiveRoomView>(ROOT + '/rooms', 'POST', this.config)); }
    finally { this.loading = false; }
  }
  private async editName() {
    const value = await this.host.platform.prompt('派对房间名', '1 至 16 个字', this.config.name);
    if (value !== null) {
      const name = value.trim(); if (!name || Array.from(name).length > 16) { this.host.toast('房间名请用 1 至 16 个字。'); return; }
      this.config.name = name;
    }
  }
  private receiveState(next: LiveRoomView) {
    const previous = this.room; const roundChanged = previous?.round?.id !== next.round?.id;
    if (previous?.id === next.id && next.revision < previous.revision) return;
    if (!roundChanged) for (const member of next.members) {
      const old = previous?.members.find(m => m.id === member.id)?.bubble;
      if (old && old.revision > (member.bubble?.revision ?? 0)) member.bubble = old;
    }
    const phaseChanged = previous?.phase !== next.phase;
    this.clockOffset = next.serverTime - Date.now();
    if (roundChanged || phaseChanged) {
      this.closeKeyboard(); this.pointerId = null; this.active = undefined; this.draft = undefined; this.points = []; this.panel = null;
      this.resetGesture(); this.view.reset();
      this.selected = []; this.answer = ''; this.queuedGuess.clear(); this.pendingGuess = undefined; this.ownGuessText = '';
      if (roundChanged) { this.pending = []; this.selectionRefreshes = 0; }
      this.answerFeedback = ''; this.answerFeedbackUntil = 0;
      if (phaseChanged && next.phase !== 'drawing') this.pending = [];
    }
    this.room = next;
    if (this.editingRoomId && (next.id !== this.editingRoomId || next.ownerId !== this.host.player?.id || next.phase !== 'waiting' || next.match)) {
      this.editingRoomId = null;
      if (this.host.screen === 'live-create') { this.host.go('live-room', -1); this.host.toast('房间状态已更新，请等本局结束后再调整设置。'); }
    }
    if (roundChanged) { this.reactionTarget = next.round?.artistId ?? next.members[0]?.id ?? ''; this.reactions = []; }
    if (!next.members.some(m => m.id === this.reactionTarget)) this.reactionTarget = next.round?.artistId ?? next.members[0]?.id ?? '';
    if ((next.round?.acceleration ?? 0) > (previous?.round?.acceleration ?? 0)) this.accelerationUntil = Date.now() + 2500;
    if (!next.match && next.lastMatch && next.lastMatch.id !== this.seenSettlement) { this.seenSettlement = next.lastMatch.id; this.panel = 'settlement'; this.panelPage = 0; }
    this.loadAvatars();
    this.selectionRefreshes = next.round?.refreshes ?? this.selectionRefreshes;
    const feedback = next.feedback?.at(-1);
    if (feedback && feedback.createdAt > Date.now() + this.clockOffset - 2500) {
      const member = next.members.find(value => value.id === feedback.userId);
      this.feedbackText = (member?.nickname ? member.nickname + '：' : '') + feedback.text;
      this.feedbackUntil = feedback.createdAt - this.clockOffset + 2500;
    }
    if (next.round) {
      const seq = next.round.seq;
      this.pending = this.pending.filter(item => item.command.roundId === next.round!.id && item.command.seq > seq);
      if (roundChanged || phaseChanged || this.snapshotRequested || seq > this.receivedSeq) {
        // Keep local, unacknowledged operations on top of the authoritative checkpoint.
        this.model.reset(next.round.drawing, next.round.history); this.receivedSeq = seq;
        for (const item of this.pending) this.model.apply(item.command.operation);
        this.seq = Math.max(seq, ...this.pending.map(item => item.command.seq));
        if (this.active) { this.active = this.model.drawing.strokes.at(-1); if (this.points.length) this.model.apply({ kind: 'append', points: this.points }); }
      }
    } else { this.pending = []; this.seq = 0; this.receivedSeq = 0; }
    if (this.me()?.guessed) { if (this.pendingGuess) { this.answerFeedback = '已猜中 · 等待其他友友'; this.answerFeedbackUntil = Date.now() + 3500; } this.closeKeyboard(); this.pendingGuess = undefined; this.queuedGuess.clear(); }
    this.snapshotRequested = false;
    if (next.lastMatch?.id !== previous?.lastMatch?.id && next.lastMatch) void this.task(async () => { this.host.player = await this.host.api('/me'); });
  }
  private avatarImage(member: LiveMember) {
    const url = member.avatarUrl;
    if (!url) return undefined;
    if (this.host.avatarImageUrl === url && this.host.avatarImage) return this.host.avatarImage;
    return this.host.partnerAvatarImages.get(url) ?? this.avatars.get(url);
  }
  private loadAvatars() {
    const generation = this.avatarGeneration;
    for (const member of this.room?.members ?? []) {
      const url = member.avatarUrl;
      if (!url || !this.host.platform.loadImage || this.avatarImage(member) || this.avatarRequests.has(url) || (this.avatarRetryAt.get(url) ?? 0) > Date.now()) continue;
      this.avatarRequests.add(url);
      void this.host.platform.loadImage(url).then(image => {
        if (generation !== this.avatarGeneration) return;
        if (this.avatars.size >= 24) this.avatars.delete(this.avatars.keys().next().value!);
        this.avatars.set(url, image); this.avatarRetryAt.delete(url);
      }).catch(() => { if (generation === this.avatarGeneration) this.avatarRetryAt.set(url, Date.now() + 15000); })
        .finally(() => { if (generation === this.avatarGeneration) this.avatarRequests.delete(url); });
    }
  }
  private connect() {
    if (!this.room || this.connected || this.connecting || !this.host.token) return;
    if (!this.host.platform.connectSocket) { this.host.toast('当前客户端不支持实时连接，请更新后再加入。'); return; }
    this.connecting = true; const epoch = ++this.epoch; const roomId = this.room.id;
    const fail = () => {
      if (epoch !== this.epoch) return;
      this.finishStroke(true); this.resetGesture();
      this.connected = false; this.connecting = false; this.reconnectAt = Date.now() + 1500;
      this.queuedGuess.clear();
      this.pointerId = null; this.draft = undefined; this.active = undefined;
      if (this.points.length) { this.points = []; }
    };
    this.socket = this.host.platform.connectSocket('/api' + ROOT + '/socket', {
      onOpen: () => { if (epoch === this.epoch) void this.send({ type: 'auth', token: this.host.token, roomId, version: LIVE_PROTOCOL_VERSION, capabilities: ['progress'] }); },
      onMessage: data => {
        if (epoch !== this.epoch) return; this.lastReceived = Date.now();
        try { this.receive(JSON.parse(data) as LiveServerMessage); } catch { this.host.toast('收到的房间数据有误，正在重新同步。'); void this.send({ type: 'sync' }); }
      },
      onClose: fail,
      onError: error => { fail(); this.host.toast(error.message); },
    });
    this.lastReceived = Date.now();
  }
  private async send(message: LiveClientMessage) {
    if (message.type === 'sync') this.snapshotRequested = true;
    try { await this.socket?.send(JSON.stringify(message)); } catch { this.reconnect(); }
  }
  private receive(message: LiveServerMessage) {
    if (message.type === 'state') {
      const reconnect = !this.connected; if (reconnect) this.snapshotRequested = true;
      this.connected = true; this.connecting = false; this.receiveState(message.room);
      if (reconnect) for (const item of this.pending) { item.sent = Date.now(); void this.send({ type: 'drawing', id: item.id, command: item.command }); }
    } else if (message.type === 'progress') {
      const next = message.progress; const room = this.room;
      if (!room || next.roomId !== room.id || next.matchId !== room.match?.id || next.roundId !== room.round?.id || room.phase !== 'drawing' || next.revision < room.revision) return;
      if (next.acceleration > (room.round.acceleration ?? 0)) this.accelerationUntil = Date.now() + 2500;
      room.revision = next.revision; room.serverTime = next.serverTime; this.clockOffset = next.serverTime - Date.now();
      room.round.deadlineAt = next.deadlineAt; room.round.acceleration = next.acceleration;
      if (next.category) room.round.category = next.category;
      for (const update of next.members) { const member = room.members.find(m => m.id === update.id); if (member) { member.score = update.score; member.guessed = update.guessed; if (update.bubble && update.bubble.revision >= (member.bubble?.revision ?? 0)) member.bubble = update.bubble; } }
      if (this.me()?.guessed) { if (this.pendingGuess) { this.answerFeedback = '已猜中 · 等待其他友友'; this.answerFeedbackUntil = Date.now() + 3500; } this.closeKeyboard(); this.pendingGuess = undefined; this.queuedGuess.clear(); }
    } else if (message.type === 'bubble') {
      if (message.matchId !== this.room?.match?.id || message.roundId !== this.room.round?.id) return;
      const member = this.room.members.find(m => m.id === message.userId);
      if (member && !member.guessed && message.bubble.revision >= (member.bubble?.revision ?? 0)) member.bubble = message.bubble;
    } else if (message.type === 'reaction') {
      const r = message.reaction; const now = Date.now();
      if (r.matchId !== (this.room?.match?.id ?? this.room?.lastMatch?.id) || r.roundId !== this.room?.round?.id || this.seenReactions.has(r.id) || Math.abs(now + this.clockOffset - r.createdAt) > 3000) return;
      this.seenReactions.set(r.id, now); for (const [id, at] of this.seenReactions) if (now - at > 120_000) this.seenReactions.delete(id);
      this.reactions = [...this.reactions, { ...r, receivedAt: now }].slice(-24);
    } else if (message.type === 'drawing') {
      const command = message.command; if (command.roundId !== this.room?.round?.id || command.seq <= this.receivedSeq) return;
      if (command.seq !== this.receivedSeq + 1) { void this.send({ type: 'sync' }); return; }
      this.receivedSeq = command.seq;
      const local = this.pending.find(item => item.command.seq === command.seq);
      if (!local) { this.model.apply(command.operation); this.seq = Math.max(this.seq, command.seq); }
      this.pending = this.pending.filter(item => item.command.seq > command.seq);
      if (command.operation.kind === 'undo' || command.operation.kind === 'redo') void this.send({ type: 'sync' });
    } else if (message.type === 'ack') {
      this.pending = this.pending.filter(item => item.id !== message.id);
      if (message.result && this.queuedGuess.delete(message.id)) {
        this.pendingGuess = undefined;
        this.answerFeedback = message.result.correct ? `+${message.result.score} 分 · 星星整局结算` : '还没猜中，再想想';
        this.answerFeedbackUntil = Date.now() + 3500;
        if (message.result.correct) {
          const member = this.me(); if (member && !member.guessed) { if (!message.result.alreadyGuessed) member.score += message.result.score; member.guessed = true; member.bubble = { correct: true, revision: member.bubble?.revision ?? 0, score: message.result.score }; }
          this.closeKeyboard();
        }
      }
    } else if (message.type === 'error') {
      if (message.id) this.queuedGuess.delete(message.id);
      if (this.pendingGuess?.id === message.id) this.pendingGuess = undefined;
      this.host.toast(message.message);
      if (message.status === 401 || message.status === 403 || message.status === 404 || message.status === 426) {
        if (!this.connected || message.status === 401) { this.disconnect(); this.room = null; this.lobby(-1); }
      }
      if (this.pending.some(item => item.id === message.id)) { this.pending = []; this.points = []; this.active = undefined; this.pointerId = null; void this.send({ type: 'sync' }); }
    } else if (message.type === 'closed' || message.type === 'removed') {
      this.clear(); this.host.toast(message.reason); this.lobby(-1);
    } else if (message.type === 'pong') this.clockOffset = message.serverTime - Date.now();
  }
  private disconnect() { ++this.epoch; this.socket?.close(); this.socket = undefined; this.connected = false; this.connecting = false; }
  private reconnect() { this.disconnect(); this.reconnectAt = Date.now() + 1000; }
  resize() { this.leaveCanvas(); if(this.keyboardOpen)this.keyboardHeight=this.keyboardPixels/this.host.layout.scale; }
  leaveCanvas() { this.finishStroke(true); this.resetGesture(); this.panel = null; }
  pause() { this.leaveCanvas(); this.closeKeyboard(); this.disconnect(); }
  resume() { if (this.room) this.connect(); }
  clear() {
    this.editingRoomId = null;
    this.pendingGuess = undefined; this.queuedGuess.clear(); this.ownGuessText = '';
    this.reactions = []; this.seenReactions.clear(); this.seenSettlement = ''; this.reactionTarget = '';
    this.resetGesture(); this.view.reset();
    this.avatarGeneration++; this.avatars.clear(); this.avatarRequests.clear(); this.avatarRetryAt.clear();
    this.disconnect(); this.closeKeyboard(); this.room = null; this.pending = []; this.pointerId = null; this.slidingId = null; this.active = undefined; this.draft = undefined; this.points = []; this.panel = null;
  }
  async leave(navigate = true) {
    if (!this.room) return; this.finishStroke(); await this.host.api(this.path('/leave'), 'POST'); this.clear(); if (navigate) this.lobby(-1);
  }
  back() {
    if (this.host.screen === 'live-create') {
      if (this.editingRoomId && this.room) { this.editingRoomId = null; this.host.go('live-room', -1); }
      else this.lobby(-1);
      return;
    }
    if (this.panel) { this.panel = null; return; }
    void this.task(async () => {
      if (this.room && await this.host.platform.confirm('离开画猜派对？', '本房间的积分会保留。房主离开后自动交接，画者离开将结束本题。')) await this.leave();
    });
  }
  update(now: number) {
    if (this.host.screen === 'lobby' && now - this.lastLobby >= 5000) void this.task(() => this.refreshLobby());
    if (!this.room) return;
    if (!this.connected && !this.connecting && now >= this.reconnectAt) this.connect();
    if (this.connecting && now - this.lastReceived > 12_000) this.reconnect();
    if (!this.connected) return;
    const guess = this.pendingGuess;
    if (guess && now - guess.sent >= 2500) {
      if (guess.attempts < 2 && this.room?.round?.id === guess.command.roundId && this.canGuess()) {
        guess.sent = now; guess.attempts++; void this.send({ type: 'guess', id: guess.id, command: guess.command });
      } else { this.pendingGuess = undefined; this.queuedGuess.delete(guess.id); this.answerFeedback = '网络较慢，请再试一次'; this.answerFeedbackUntil = now + 3500; void this.send({ type: 'sync' }); }
    }
    if (now - this.lastPing >= 10_000) { this.lastPing = now; void this.send({ type: 'ping' }); }
    if (now - this.lastReceived > 25_000) this.reconnect();
    if (this.points.length && now - this.lastSend >= 65) this.flushPoints();
    const delayed = this.pending.find(item => now - item.sent >= 3000);
    if (delayed) { delayed.sent = now; void this.send({ type: 'drawing', id: delayed.id, command: delayed.command }); }
  }
  private operation(operation: LiveDrawingOperation, apply = true) {
    const round = this.room?.round; if (!round) return;
    const command: LiveDrawingCommand = { roundId: round.id, seq: ++this.seq, operation };
    const item = { id: requestId(), command, sent: Date.now() }; this.pending.push(item);
    if (apply) this.model.apply(operation);
    void this.send({ type: 'drawing', id: item.id, command });
  }
  private flushPoints() {
    if (!this.points.length) return;
    while (this.points.length) this.operation({ kind: 'append', points: this.points.splice(0, 100) }, false);
    this.lastSend = Date.now();
  }
  private finishStroke(cancelled = false) {
    if (this.pendingInk) {
      if (!cancelled) this.commitPendingInk();
      else { this.pendingInk = undefined; this.draft = undefined; this.bufferRevision = -1; }
    }
    if (this.lineFrom && this.lineTo) {
      const frozen=this.lineOptions;const from = this.lineFrom; const to = frozen?.brush === 'line' ? snappedLine(from, this.lineTo) : this.lineTo;
      this.cancelLine();
      if (!cancelled && this.canDraw() && Math.hypot(to.x - from.x, to.y - from.y) >= 6) {
        this.beginStroke(from,false,frozen);
        this.appendPoint({ x: Math.max(0, Math.min(1000, to.x)), y: Math.max(0, Math.min(1000, to.y)) });
      }
    }
    if (!this.active) { this.pointerId = null; return; }
    this.flushPoints(); const stroke = this.active; this.operation({ kind: 'end' });
    if (stroke.brush === 'mirror' && stroke.tool !== 'eraser') {
      const mirrored = { ...stroke, points: stroke.points.map(point => ({ ...point, x: 1000 - point.x })) };
      this.operation({ kind: 'begin', stroke: { ...mirrored, points: mirrored.points.slice(0, 1) } });
      for (let i = 1; i < mirrored.points.length; i += 100) this.operation({ kind: 'append', points: mirrored.points.slice(i, i + 100) });
      this.operation({ kind: 'end' });
    }
    this.active = undefined; this.draft = undefined; this.pointerId = null;
  }
  private cancelLine() { this.lineFrom = undefined; this.lineTo = undefined; this.lineOptions=undefined; }
  private resetGesture() { this.view.clearGesture(); this.pendingInk = undefined; this.bufferRevision = -1; this.consumed.clear(); this.slidingId = null; this.cancelLine(); }
  private toolsLayout() { return drawingToolLayout(this.board.toolsY, this.board.toolsHeight ?? 150, this.board.size, this.host.W); }
  private strokeOptions(): import('../../shared/art/drawing').StrokeOptions { return { color: this.color, width: this.eraser ? this.eraserWidth : this.width,
      ...this.host.drawingSettings.stroke(this), brush: this.brush, ...(this.eraser ? { tool: 'eraser' } : {}),
      ...(!this.eraser && this.brush === 'shape' ? { shape: this.shape, ...(this.shapeFilled ? { opacity: SHAPE_FILL_OPACITY } : {}) } : {}) }; }
  private beginStroke(pos: { x: number; y: number }, previewOnly = false, frozen?: import('../../shared/art/drawing').StrokeOptions) {
    this.draft = new DrawingEditor();
    this.draft.start(pos.x, pos.y, frozen ?? this.strokeOptions(), Date.now());
    const stroke = this.draft.document.strokes[0]; stroke.points[0].t = Math.max(this.strokeTime(), (this.model.drawing.strokes.at(-1)?.points.at(-1)?.t ?? -1) + 1);
    if (stroke.tool === 'line' || stroke.tool === 'shape') stroke.points.push({ ...stroke.points[0] });
    // Show the initial dot locally. A second finger can cancel it without
    // sending a phantom stroke to the room or overwriting its redo history.
    if (previewOnly) { this.pendingInk = stroke; this.bufferRevision = -1; return; }
    this.operation({ kind: 'begin', stroke }); this.active = this.model.drawing.strokes.at(-1);
  }
  private commitPendingInk() {
    const stroke = this.pendingInk; if (!stroke) return;
    this.pendingInk = undefined; this.operation({ kind: 'begin', stroke }); this.active = this.model.drawing.strokes.at(-1);
  }
  private strokeTime() { return Math.max(0, Math.min(3_600_000, Math.round(Date.now() + this.clockOffset - (this.room!.round!.startedAt ?? this.room!.serverTime)))); }
  private appendPoint(pos: { x: number; y: number }) {
    const last = this.active?.points.at(-1);
    if(last && this.active?.brushVersion===2 && this.active.tool!=='line' && this.active.tool!=='shape'){const k=1-(this.active.settings?.stabilize??0)/125;pos={x:last.x+(pos.x-last.x)*k,y:last.y+(pos.y-last.y)*k};}
    if (this.active && last && Math.hypot(pos.x - last.x, pos.y - last.y) >= 2 && this.active.points.length < 1500) {
      const next = { ...pos, t: Math.max(last.t + 1, this.strokeTime()) };
      this.model.apply({ kind: 'append', points: [next] }); this.active = this.model.drawing.strokes.at(-1); this.points.push(next);
    }
  }
  pointer(kind: 'down' | 'move' | 'up' | 'cancel', point: Pointer): boolean {
    if (this.host.screen !== 'live-room') return false;
    const local = point; const { x, y } = local;
    const b = this.board; const inside = this.view.contains(b, local);
    if (kind === 'down' && point.touches?.length === 1 && point.touches[0].id === point.id) {
      if (this.pointerId !== null) this.finishStroke(true);
      this.slidingId = null; this.consumed.clear();
    }
    const capturedGesture = this.view.capturing ? this.view.pointer(kind, local, b) : undefined;
    if (capturedGesture?.consumed) return true;
    if (this.consumed.has(point.id)) { if (kind === 'up' || kind === 'cancel') this.consumed.delete(point.id); return true; }
    if (this.slidingId === point.id) {
      if (this.canDraw() && kind !== 'cancel') this.setWidth(x);
      if (kind === 'up' || kind === 'cancel') this.slidingId = null;
      return true;
    }
    if (this.slidingId !== null) return true;
    const slider = this.toolsLayout();
    if (kind === 'down' && this.pointerId === null && (!this.panel || this.panel === 'brush') && this.canDraw() && y >= slider.sliderY - 22 && y <= slider.sliderY + 22 && x >= 24 && x <= this.host.W - 24) {
      this.slidingId = point.id; this.setWidth(x); return true;
    }
    if (kind === 'down' && this.panel) {
      this.consumed.add(point.id);
      const hit = [...this.host.paint.hits].reverse().find(h => !h.disabled && x >= h.x && x <= h.x + h.w && y >= h.y && y <= h.y + h.h);
      this.panel = null; hit?.action(); return true;
    }
    if (!this.canDraw()) return false;
    const gesture = capturedGesture ?? this.view.pointer(kind, local, b);
    if (gesture.started) {
      // Cancel an unfinished drag preview; only undo if a streamed stroke exists.
      const hadStroke = !!this.active; this.finishStroke(true); if (hadStroke) this.operation({ kind: 'undo' });
      this.host.paint.pressedId = null;
    }
    if (gesture.consumed) return true;
    if (this.pointerId === point.id) {
      if (kind === 'move' || kind === 'up') {
        const pos = this.position(x, y);
        const first = this.pendingInk?.points[0];
        if (first && (kind === 'up' || Math.hypot(pos.x - first.x, pos.y - first.y) >= 2)) this.commitPendingInk();
        if (this.lineFrom) this.lineTo = pos; else this.appendPoint(pos);
      }
      if (kind === 'up' || kind === 'cancel') this.finishStroke(kind === 'cancel');
      return true;
    }
    if (this.pointerId !== null) return true;
    if (kind !== 'down' || !inside) return false;
    if (!this.view.containsPaper(b, local)) return true;
    this.pointerId = point.id; const pos = this.position(x, y);
    if (!this.eraser && DRAG_BRUSHES.includes(this.brush)) { this.lineOptions=this.strokeOptions();this.lineFrom = pos; this.lineTo = pos; }
    else this.beginStroke(pos, true);
    return true;
  }
  private position(x: number, y: number) {
    return this.view.toDocument(this.board, { x, y }, true);
  }

  private setWidth(x: number) { const width = brushWidthForRatio((x - 94) / (this.toolsLayout().sliderRight - 94)); if (this.eraser) this.eraserWidth = width; else this.width = width; this.host.drawingSettings.width(this,width); }
  private async choose(wordId: string) {
    const round = this.room?.round; if (!round || !this.connected) return;
    await this.send({ type: 'choose', id: requestId(), roundId: round.id, wordId });
  }
  private async refreshChoices() {
    const view = await this.host.api<LiveRoomView>(this.path('/choices'), 'POST', { roundId: this.room!.round!.id }); this.receiveState(view);
  }
  private async guess(text?: string) {
    if (!this.canGuess() || this.queuedGuess.size) return; const round = this.room!.round!;
    const command = text === undefined ? { roundId: round.id, indices: [...this.selected] } : { roundId: round.id, answer: text.trim() };
    const id = requestId(); this.queuedGuess.add(id);
    this.ownGuessText = text?.trim() ?? this.selected.map((index, slot) => round.banks?.[slot]?.[index] ?? '').join('');
    this.answerFeedback = '已提交，正在判题…'; this.answerFeedbackUntil = Date.now() + 5000;
    this.pendingGuess = { id, command, sent: Date.now(), attempts: 1 }; await this.send({ type: 'guess', id, command });
  }
  closeKeyboard() { if (this.keyboardOpen) this.host.platform.closeKeyboard?.(); this.keyboardOpen = false; this.keyboardHeight = 0; }
  private async keyboard() {
    if (!this.canGuess() || this.keyboardOpen) return;
    if (!this.host.platform.openKeyboard) { const value = await this.host.platform.prompt('猜猜是什么', '输入中文答案', this.answer); if (value) await this.guess(value); return; }
    this.keyboardOpen = true;
    try {
      await this.host.platform.openKeyboard({ value: this.answer, maxLength: 40,
        onInput: value => { this.answer = value; },
        onConfirm: value => { this.answer = value; void this.task(() => this.guess(value)); },
        onClose: () => { this.keyboardOpen = false; this.keyboardHeight = 0; },
        onHeight: height => { this.keyboardPixels=height; this.keyboardHeight = height / this.host.layout.scale; },
      });
    } catch (error) { this.closeKeyboard(); throw error; }
  }
  private async share() { if (this.room) await this.host.platform.share(this.room.code, { title: `来「${this.room.name}」一起画猜`, mode: 'live-draw', room: true }); }
  private async manage() {
    if (!this.room) return;
    const owner = this.ownsRoom();
    const actions = owner ? ['邀请好友', '积分榜', '快捷回应', '移出成员', '举报画作', '关闭房间', '离开房间'] : ['邀请好友', '积分榜', '快捷回应', '举报画作', '离开房间'];
    if (owner && this.room.phase === 'waiting' && !this.room.match) actions.splice(1, 0, '房间设置');
    const selected = await this.host.platform.choose(this.room.name, actions); if (selected === null) return;
    const action = actions[selected];
    if (action === '邀请好友') await this.share();
    else if (action === '房间设置') this.settingsScreen();
    else if (action === '积分榜') this.panel = this.room.lastMatch && !this.room.match ? 'settlement' : 'ranking';
    else if (action === '快捷回应') this.panel = 'feedback';
    else if (action === '离开房间') this.back();
    else if (action === '关闭房间') {
      if (await this.host.platform.confirm('关闭整个房间？', '所有成员将离开房间，已有画作仍保存在画廊。')) { await this.host.api(this.path('/close'), 'POST'); this.clear(); this.lobby(-1); }
    } else if (action === '移出成员') {
      const members = this.room.members.filter(member => member.id !== this.host.player?.id);
      const index = await this.host.platform.choose('移出成员', members.map(member => member.nickname));
      if (index !== null && members[index] && await this.host.platform.confirm('移出这位友友？', '本房间关闭前，对方将无法重新加入。')) await this.host.api(this.path('/kick'), 'POST', { userId: members[index].id });
    } else if (action === '举报画作') {
      const reasons = ['不适当内容', '隐私问题', '直接写答案', '其他问题']; const index = await this.host.platform.choose('举报原因', reasons);
      if (index !== null) { await this.host.api(this.path('/reports'), 'POST', { reason: reasons[index] }); this.host.toast('举报已收到。'); }
    }
  }
  private async feedback(index: number) {
    if (Date.now() - this.lastFeedback < 3000) return;
    await this.host.api(this.path('/feedback'), 'POST', { text: FEEDBACK[index] }); this.lastFeedback = Date.now(); this.panel = null;
  }
  private lobbyPageSize() { return Math.max(1, Math.floor((this.host.H - this.host.top - 294 - (this.room ? 44 : 0)) / 102)) * (this.host.tablet ? 2 : 1); }
  render(now: number) {
    this.loadAvatars();
    if (this.host.screen === 'lobby') this.renderLobby();
    else if (this.host.screen === 'live-create') this.renderCreate();
    else if (this.host.screen === 'live-room') this.renderRoom(now);
  }
  private ruleLine(x: number, y: number, width = 342) { this.host.paint.dashed(this.host.paint.x(x), y, this.host.paint.x(x + width), y, C.line, 1, [3, 5]); }
  private avatar(member: LiveMember | undefined, x: number, y: number, radius: number) {
    const p = this.host.paint; const image = member ? this.avatarImage(member) : undefined;
    p.circle(x, y, radius + 2, C.white, C.line);
    p.circle(x, y, radius, member ? C.tint : C.cream);
    if (image) {
      p.ctx.save(); p.ctx.beginPath(); p.ctx.arc(x, y, radius, 0, Math.PI * 2); p.ctx.clip(); p.ctx.drawImage(image, x - radius, y - radius, radius * 2, radius * 2); p.ctx.restore();
    } else if (member) p.text([...member.nickname][0] ?? '友', x, y, radius >= 25 ? T.title : T.body, C.accentDeep, '700', 'center');
    else p.icon('plus', x, y, radius, C.mute);
  }
  private memberStatus(member: LiveMember) {
    if (!member.connected) return '重连中';
    if (!this.room?.round) return member.id === this.room?.ownerId ? '房主' : '已就座';
    if (member.id === this.room.round.artistId) return this.room.phase === 'choosing' ? '选词中' : this.room.phase === 'revealing' ? '本题画者' : '正在画';
    if (member.spectator) return '下一题加入';
    if (member.guessed) return '已猜中';
    return this.room.phase === 'revealing' ? '本题结束' : '思考中';
  }
  private renderSeats(y: number, seatHeight = this.board.seatHeight) {
    const p = this.host.paint; const radius = this.host.tablet && !this.keyboardOpen ? 21 : seatHeight >= 70 ? 17 : 12;
    this.seatPositions.clear();
    for (let i = 0; i < LIVE_CAPACITY; i++) {
      const member = this.room!.members[i]; const x = this.host.tablet ? 24 + (i % 5 + .5) * (this.host.W - 48) / 5 : 51 + (i % 5) * 72; const top = y + Math.floor(i / 5) * seatHeight; const cy = top + radius;
      if (member?.id === this.reactionTarget) p.circle(x, cy, radius + 3, C.gold);
      this.avatar(member, x, cy, radius);
      if (!member) { p.text('空位', x, top + radius * 2 + 7, T.tiny, C.mute, '400', 'center'); continue; }
      this.seatPositions.set(member.id, { x, y: cy });
      const c = p.ctx; c.save(); c.beginPath(); c.arc(x, cy, radius, 0, Math.PI * 2); c.clip();
      c.fillStyle = 'rgba(35,48,58,.76)'; c.fillRect(x - radius, cy + radius - 12, radius * 2, 12);
      p.fit(String(member.score), x, cy + radius - 6, radius * 2 - 3, T.tiny, C.white, '700', 'center'); c.restore();
      p.fit(member.nickname, x, top + radius * 2 + 7, 66, T.tiny, member.id === this.host.player?.id ? C.accentDeep : C.ink, '500', 'center');
      if (member.id === this.room!.round?.artistId) { p.circle(x + radius, cy - radius + 2, 6, C.cream); p.icon('brush', x + radius, cy - radius + 2, 9, C.orange); }
      else if (!member.connected || member.spectator) { p.circle(x + radius, cy - radius + 2, 4, member.connected ? C.gold : C.mute); }
      p.hits.push({ id: 'live-target-' + member.id, label: `向${member.nickname}发送互动，${member.score}分`, x: x - 31, y: top, w: 62, h: radius * 2 + 13, action: () => { this.reactionTarget = member.id; } });
      const bubbleWidth=this.host.tablet?Math.min(120,(this.host.W-48)/5-16):62;
      const bubbleY = top + radius * 2 + 15; const bubbleH = seatHeight - radius * 2 - 17;
      // Own input can be echoed immediately; only server-approved text reaches other players.
      const bubble = member.id === this.host.player?.id && this.ownGuessText && !member.guessed ? { text: this.ownGuessText, correct: false, revision: 0 } : member.bubble;
      if (member.guessed || bubble?.correct) {
        const score = member.bubble?.score ?? this.room!.round?.results?.find(result => result.userId === member.id)?.score;
        p.box(x - bubbleWidth/2, bubbleY, bubbleWidth, bubbleH, '#E3F1E4', 6);
        p.icon('check', score === undefined ? x : x - 18, bubbleY + bubbleH / 2, 13, '#327E66');
        if (score !== undefined) p.fit(`+${score}分`, x + 9, bubbleY + bubbleH / 2, bubbleWidth-30, T.caption, '#327E66', '700', 'center');
      } else if (bubble?.text) {
        p.box(x - bubbleWidth/2, bubbleY, bubbleWidth, bubbleH, C.white, 6, C.line);
        const letters = Array.from(bubble.text); const first = letters.slice(0, 5).join(''); const second = letters.slice(5, letters.length > 10 ? 9 : 10).join('') + (letters.length > 10 ? '…' : '');
        p.text(first, x, bubbleY + (second ? bubbleH / 2 - 5 : bubbleH / 2), T.tiny, C.ink, '400', 'center');
        if (second) p.text(second, x, bubbleY + bubbleH / 2 + 5, T.tiny, C.ink, '400', 'center');
        p.hits.push({ id: 'live-bubble-' + member.id, label: `${member.nickname}猜：${bubble.text}`, x: x - bubbleWidth/2, y: bubbleY, w: bubbleWidth, h: bubbleH, action: () => { this.bubbleText = `${member.nickname}：${bubble.text}`; this.panel = 'bubble'; } });
      } else if (!member.connected || member.spectator) p.text(!member.connected ? '重连中' : '下一题加入', x, bubbleY + bubbleH / 2, T.tiny, C.mute, '400', 'center');
    }
  }
  private async react(kind: LiveReactionKind) {
    const room = this.room; const matchId = room?.match?.id ?? room?.lastMatch?.id; const now = Date.now();
    if (!room || !matchId || !this.connected || now - this.lastReaction < LIVE_REACTION_COOLDOWN) return;
    this.lastReaction = now;
    await this.host.api(this.path('/reactions'), 'POST', { matchId, roundId: room.round?.id, targetUserId: this.reactionTarget, kind });
  }
  private renderReactions(now: number) {
    if (this.keyboardOpen || !this.room || (!this.room.match && !this.room.lastMatch)) return;
    const p = this.host.paint; const h = this.host.H - this.host.top; const y = h - 51;
    this.ruleLine(24, y - 8, 342);
    const target = this.room.members.find(m => m.id === this.reactionTarget);
    p.text('送给', p.x(25), y + 5, T.tiny, C.mute); p.fit(target?.nickname ?? '点头像选人', p.x(25), y + 21, p.span(98), T.caption, C.accentDeep, '600');
    const labels = ['玫瑰', '鸡蛋', '点赞', '点踩'];
    LIVE_REACTIONS.forEach((kind, index) => {
      const x = this.host.x(150 + index * 63); const cooling = now - this.lastReaction < LIVE_REACTION_COOLDOWN;
      p.box(x - 26, y - 1, 53, 40, cooling ? C.paper : C.cream, 10, C.line);
      reactionIcon(p.ctx, kind, x - 8, y + 13, 19); p.text(labels[index], x + 10, y + 28, T.tiny, C.ink, '400', 'center');
      if (cooling) { p.ctx.save(); p.ctx.strokeStyle = C.gold; p.ctx.lineWidth = 2; p.ctx.beginPath(); p.ctx.arc(x - 8, y + 13, 12, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (now - this.lastReaction) / LIVE_REACTION_COOLDOWN); p.ctx.stroke(); p.ctx.restore(); }
      p.hits.push({ id: 'live-reaction-' + kind, label: `送出${labels[index]}`, x: x - 26, y: y - 1, w: 53, h: 40, disabled: cooling || !this.connected || !target, action: () => void this.task(() => this.react(kind)) });
    });
  }
  private renderReactionAnimations(now: number) {
    this.reactions = this.reactions.filter(r => now - r.receivedAt < 1600);
    const h = this.host.H - this.host.top;
    for (const r of this.reactions) {
      const target = this.seatPositions.get(r.targetUserId); if (!target) continue;
      const index = LIVE_REACTIONS.indexOf(r.kind);
      reactionFlight(this.host.ctx, r.kind, r.id, now - r.receivedAt, { x: 142 + index * 63, y: h - 38 }, target);
    }
  }
  private renderRoom(now: number) {
    const room = this.room; if (!room) { this.host.header('画猜派对'); return; }
    const p = this.host.paint; const h = this.host.H - this.host.top; const round = room.round;
    this.host.header('画猜派对');
    this.icon('room-menu', '房间菜单', 'gear', 244, 32, () => void this.task(() => this.manage()));
    this.board = liveBoardLayout(h, this.keyboardHeight, room.role === 'artist' && room.phase === 'drawing', this.host.W, this.host.tablet);
    const b = this.board;
    if (!this.keyboardOpen) {
      p.fit(now < this.feedbackUntil ? this.feedbackText : room.name, p.x(26), 67, p.span(225), T.caption, now < this.feedbackUntil ? C.accentDeep : C.mute);
      p.text(`${room.memberCount}/${LIVE_CAPACITY} · ${room.match ? `${room.match.current}/${room.match.total}题` : '等候中'}`, p.x(365), 67, T.caption, this.connected ? C.accentDeep : C.orange, '500', 'right');
      p.hits.push({ id: 'live-members', label: '查看房间成员与积分', x: p.x(262), y: 54, w: p.span(105), h: 24, action: () => { this.panel = 'members'; } });
    }
    if (room.phase === 'waiting') {
      this.renderWaiting(h - (room.lastMatch ? 60 : 0));
      if (this.panel) this.renderPanel(); this.renderReactions(now); this.renderReactionAnimations(now); return;
    }
    if (!round) return;
    const seconds = Math.max(0, Math.ceil((round.deadlineAt - now - this.clockOffset) / 1000));
    const titleY = b.titleY;
    const title = room.phase === 'choosing' ? room.role === 'artist' ? '挑一个想画的词' : '等待画者选词' : room.phase === 'revealing' ? '这一题，原来是…' : room.role === 'artist' ? round.word ?? '' : `${round.letterCount ?? ''} 个字${round.category ? ' · ' + round.category : ''}`;
    p.box(p.x(24), titleY - 13, p.span(250), 27, C.white, 12, C.line);
    p.fit(title, p.x(38), titleY, p.span(226), T.bodyStrong, C.accentDeep, '700');
    if (room.role === 'artist' && this.view.changed && room.phase === 'drawing') this.button('reset-zoom', '恢复', 222, titleY - 12, 49, 24, () => { this.view.reset(); });
    p.text(room.match?.waitingForPlayers ? '等重连' : `${seconds} 秒`, p.x(365), titleY, T.section, seconds <= 10 ? C.red : C.accentDeep, '700', 'right');
    if (now < this.accelerationUntil && round.acceleration) p.text(round.acceleration === 2 ? '八成猜中 · 最多剩15秒' : '过半猜中 · 最多剩30秒', p.x(195), titleY + 18, T.tiny, C.orangeDeep, '500', 'center');
    if (room.phase === 'choosing') { this.renderChoosing(h); this.renderSeats(b.seatsY); if (this.panel) this.renderPanel(); this.renderReactions(now); this.renderReactionAnimations(now); return; }
    if (this.bufferRevision !== this.model.revision) {
      const preview = this.pendingInk ?? this.active;
      const strokes = this.pendingInk ? [...this.model.drawing.strokes, this.pendingInk] : this.model.drawing.strokes;
      const drawing = preview?.brush === 'mirror' && preview.tool !== 'eraser'
        ? { ...this.model.drawing, strokes: [...strokes, { ...preview, points: preview.points.map(point => ({ ...point, x: 1000 - point.x })) }] }
        : this.pendingInk ? { ...this.model.drawing, strokes } : this.model.drawing;
      renderDrawing(this.buffer.getContext('2d')!, drawing, 1000); this.bufferRevision = this.model.revision;
    }
    p.box(b.x - 5, b.y - 5, b.size + 10, b.size + 10, C.white, 14, undefined, 12);
    const view = room.role === 'artist' && room.phase === 'drawing' ? this.view : this.defaultView;
    view.render(this.host.ctx, b, this.buffer);
    if (this.lineFrom && this.lineTo) renderDragPreview(this.host, this, b, view, this.lineFrom, this.lineTo, this.lineOptions);
    if (!this.keyboardOpen && b.size > 150) { p.tape(b.x + 25, b.y - 3, -0.22, 33, 9); p.tape(b.x + b.size - 25, b.y - 3, 0.22, 33, 9); }
    if (room.phase === 'revealing') this.renderReveal(b.seatsY + 40);
    else if (room.role === 'artist') this.renderTools();
    else this.renderGuess(now);
    this.renderSeats(b.seatsY);
    this.renderReactions(now); this.renderReactionAnimations(now);
    if (this.panel) this.renderPanel();
    if (room.phase === 'drawing') {
      // Screen edges include the top safe area and stop above the native keyboard.
      p.ctx.save();
      const {dpr,width,height}=this.host.platform;
      p.ctx.setTransform(dpr,0,0,dpr,0,0);
      countdownEdges(p.ctx, height - (this.keyboardOpen?this.keyboardHeight*this.host.layout.scale:0), round.deadlineAt - now - this.clockOffset, now, width);
      p.ctx.restore();
    }
  }
  private renderWaiting(h: number) {
    const p = this.host.paint; const small = h < 600;
    p.title('友友们，就等你啦', p.x(26), 108, T.title);
    p.fit(`${this.room!.duration} 秒 / 题 · ${ANSWER_LABELS[this.room!.answerMode]}`, p.x(26), 140, this.room!.lastMatch ? 191 : 342, T.sub, C.mute);
    this.renderSeats(small ? 166 : 182, small ? 66 : 78);
    const y = Math.max(359, h - 160);
    this.ruleLine(25, y - 16, 340);
    p.text('房间邀请', p.x(25), y + 7, T.note, C.mute);
    p.stamp(p.x(104), y - 9, 218, 33); p.fit(this.room!.code, p.x(213), y + 7, p.span(198), T.bodyStrong, C.accentDeep, '700', 'center');
    this.icon('copy-code', '复制房间码', 'copy', 347, y + 7, () => void this.task(async () => { await this.host.platform.copyText?.(this.room!.code); this.host.toast('房间码已复制。'); }), !this.host.platform.copyText);
    this.button('share-room', '邀请好友', 24, h - 107, this.ownsRoom() ? 216 : 342, 35, () => void this.task(() => this.share()), false, false, 'share');
    if (this.ownsRoom()) this.button('settings', '房间设置', 250, h - 107, 116, 35, () => this.settingsScreen(), false, !this.connected || !!this.room!.match, 'gear');
    if (this.room!.lastMatch) this.button('last-results', '查看上局结算', 228, 126, 138, 28, () => { this.panel = 'settlement'; this.panelPage = 0; });
    this.button('start', this.ownsRoom() ? '开始画猜' : '等待房主开始', 24, h - 61, 342, 43, () => void this.task(async () => this.receiveState(await this.host.api<LiveRoomView>(this.path('/start'), 'POST'))), true, !this.ownsRoom() || !this.connected || this.room!.members.filter(member => member.connected).length < 2, 'brush');
  }
  private renderChoosing(h: number) {
    const p = this.host.paint; const room = this.room!; const round = room.round!;
    if (room.role === 'artist') {
      const top = this.board.y; const rowH = Math.max(44, Math.min(88, (this.board.seatsY - top - 86) / 3));
      (round.choices ?? []).forEach((choice, index) => {
        const y = top + index * (rowH + 12);
        p.card(p.x(24), y, p.span(342), rowH, 18); p.tape(p.x(47), y + 1, -0.32, 34, 10);
        p.fit(choice.text, p.x(43), y + rowH * 0.36, p.span(211), T.title, C.ink, '600');
        p.text(choice.category, p.x(44), y + rowH * 0.74, T.note, C.mute);
        this.button('choose-' + choice.id, '画它', 276, y + (rowH - 36) / 2, 72, 36, () => void this.task(() => this.choose(choice.id)), false, !this.connected, 'brush');
      });
      const y = top + 3 * (rowH + 12) + 2;
      this.button('refresh-choices', this.selectionRefreshes < 2 ? `换一批题目 · 免费 ${2 - this.selectionRefreshes} 次` : '换一批题目 · 1 星', 24, y, 342, 40, () => void this.task(() => this.refreshChoices()), false, !this.connected || (this.selectionRefreshes >= 2 && (this.host.player?.coins ?? 0) < 1), 'refresh');
      if (this.board.seatsY - y > 170) { p.dashed(p.x(119), y + 100, p.x(271), y + 100, C.line, 1, [3, 6]); p.plane(p.x(195), y + 99, -0.15, 0.64, C.accent); }
    } else {
      const artist = room.members.find(member => member.id === round.artistId); const cy = Math.min(this.board.seatsY - 140, this.board.y + 76);
      p.stamp(p.x(135), cy - 61, 120, 124); this.avatar(artist, this.host.W / 2, cy - 18, 33);
      p.fit(artist?.nickname ?? '友友', p.x(195), cy + 37, p.span(104), T.sub, C.accentDeep, '600', 'center');
      p.text(room.role === 'spectator' ? '下一题，和大家一起猜' : 'TA 正在挑选这次的灵感', p.x(195), cy + 96, T.section, C.ink, '600', 'center');
      p.dashed(p.x(124), cy + 131, p.x(266), cy + 131, C.line, 1, [3, 5]);
    }
  }
  private renderLobby() {
    const p = this.host.paint; const h = this.host.H - this.host.top;
    this.host.header('派对大厅', undefined, false);
    p.text('正在相聚', p.x(26), 83, T.section, C.ink, '600');
    p.text(`${this.rooms.length} 个公开房间`, p.x(125), 84, T.caption, C.mute);
    this.icon('lobby-refresh', '刷新房间', 'refresh', 344, 82, () => void this.task(() => this.refreshLobby()), this.loading);
    this.button('create', '开启新派对', 24, 117, 184, 43, () => this.createScreen(), true, false, 'plus');
    this.button('join-code', '凭房间码加入', 220, 117, 146, 43, () => void this.task(() => this.promptJoin()), false, false, 'copy');
    let top = 183;
    if (this.room) { this.button('return-room', '回到 ' + [...this.room.name].slice(0, 10).join(''), 24, top, 342, 34, () => this.host.go('live-room'), false, false, 'undo'); top += 44; }
    const size = this.lobbyPageSize(); const pages = Math.max(1, Math.ceil(this.rooms.length / size)); this.lobbyPage = Math.min(this.lobbyPage, pages - 1);
    const visible = this.rooms.slice(this.lobbyPage * size, (this.lobbyPage + 1) * size);
    if (!visible.length) {
      p.doodle(p.x(195), Math.min(h - 245, top + 107), 0.8);
      p.text(this.loading ? '正在整理房间' : '第一桌派对，等你开场', p.x(195), Math.min(h - 120, top + 216), T.section, C.ink, '600', 'center');
    }
    visible.forEach((room, index) => {
      if (this.host.tablet) {
        const width = (this.host.W - 64) / 2, x = 24 + index % 2 * (width + 16), y = top + Math.floor(index / 2) * 102;
        p.card(x,y,width,90,16);p.fit(room.name,x+16,y+22,width-32,T.cardStrong,C.ink,'600');
        p.fit(`${room.memberCount}/10 · ${room.duration} 秒 · ${ANSWER_LABELS[room.answerMode]}`,x+16,y+46,width-130,T.caption,C.mute);
        p.text(room.phase==='waiting'?'等待开场':'正在画猜',x+16,y+71,T.caption,C.mute);
        p.button('live-join-'+room.id,room.memberCount>=LIVE_CAPACITY?'已满员':'入座',x+width-104,y+38,88,44,()=>void this.task(()=>this.join(room.id)),'secondary',room.memberCount>=LIVE_CAPACITY);
        return;
      }
      const y = top + index * 102; const waiting = room.phase === 'waiting';
      p.card(p.x(24), y, p.span(342), 90, 16); p.tape(p.x(47), y + 1, -0.22, 30, 8);
      p.fit(room.name, p.x(41), y + 24, p.span(237), T.cardStrong, C.ink, '600');
      p.text(`${room.memberCount}/${LIVE_CAPACITY}`, p.x(349), y + 24, T.sub, C.accentDeep, '700', 'right');
      p.text(`${room.duration} 秒 · ${ANSWER_LABELS[room.answerMode]}`, p.x(42), y + 49, T.note, C.mute);
      p.circle(p.x(44), y + 72, 3, waiting ? '#327E66' : C.orange);
      p.text(waiting ? '等待开场' : '正在画猜', p.x(55), y + 72, T.note, C.mute);
      for (let seat = 0; seat < LIVE_CAPACITY; seat++) p.circle(148 + seat * 9, y + 72, 3, seat < room.memberCount ? C.accent : C.line);
      this.button('join-' + room.id, room.memberCount >= LIVE_CAPACITY ? '已满员' : '入座', 274, y + 47, 76, 31, () => void this.task(() => this.join(room.id)), false, room.memberCount >= LIVE_CAPACITY);
    });
    const py = h - 110; this.icon('lobby-prev', '上一页', 'undo', 148, py, () => { this.lobbyPage--; }, this.lobbyPage === 0);
    p.text(`${this.lobbyPage + 1} / ${pages}`, p.x(195), py, T.caption, C.mute, '400', 'center');
    this.icon('lobby-next', '下一页', 'redo', 242, py, () => { this.lobbyPage++; }, this.lobbyPage + 1 >= pages);
    this.host.navigation('lobby');
  }
  private renderCreate() {
    const p = this.host.paint; const h = this.host.H - this.host.top; const compact = h < 600;
    const editing = !!this.editingRoomId;
    this.host.header(editing ? '房间设置' : '开启画猜派对');
    p.text('给这一桌起个名字', p.x(25), 87, T.sub, C.mute);
    p.box(p.x(24), 107, p.span(342), 47, C.white, 8, C.line, 4); p.tape(p.x(48), 107, -0.2, 31, 8);
    p.fit(this.config.name, p.x(40), 131, p.span(274), T.cardStrong, C.ink, '600');
    p.hits.push({ id: 'live-name', label: '修改房间名', x: p.x(24), y: 107, w: p.span(287), h: 47, action: () => void this.task(() => this.editName()) });
    this.icon('random-name', '换一个房间名', 'refresh', 340, 131, () => { this.config.name = LIVE_ROOM_NAMES[(LIVE_ROOM_NAMES.indexOf(this.config.name as typeof LIVE_ROOM_NAMES[number]) + 1 + Math.floor(Math.random() * (LIVE_ROOM_NAMES.length - 1))) % LIVE_ROOM_NAMES.length]; });
    const timeY = compact ? 180 : 192;
    p.text('每题绘画时间', p.x(25), timeY, T.sub, C.ink, '600');
    p.box(p.x(24), timeY + 18, p.span(342), 41, C.cream, 8, C.line);
    LIVE_DURATIONS.forEach((seconds, index) => {
      const x = this.host.x(28 + index * 84); const active = this.config.duration === seconds;
      if (active) p.box(x, timeY + 22, p.span(82), 33, C.tint, 6);
      p.text(`${seconds} 秒`, x + this.host.span(41), timeY + 39, T.sub, active ? C.accentDeep : C.mute, active ? '700' : '500', 'center');
      p.hits.push({ id: 'live-duration-' + seconds, label: seconds + ' 秒', x, y: timeY + 18, w: p.span(84), h: 41, action: () => { this.config.duration = seconds; } });
    });
    const answerY = timeY + (compact ? 86 : 100); const rowH = this.host.tablet ? 52 : compact ? 34 : 43;
    p.text('回答方式', p.x(25), answerY, T.sub, C.ink, '600');
    (['candidates', 'text', 'both'] as LiveAnswerMode[]).forEach((mode, index) => {
      const y = answerY + 21 + index * rowH; const active = this.config.answerMode === mode;
      if (active) p.box(p.x(24), y, p.span(342), rowH - 3, C.tint, 6);
      p.circle(p.x(43), y + (rowH - 3) / 2, 7, C.white, active ? C.accentDeep : C.line); if (active) p.circle(p.x(43), y + (rowH - 3) / 2, 3.5, C.accentDeep);
      p.text(ANSWER_LABELS[mode], p.x(65), y + (rowH - 3) / 2, T.body, active ? C.accentDeep : C.ink, active ? '600' : '400');
      p.hits.push({ id: 'live-answer-' + mode, label: ANSWER_LABELS[mode], x: p.x(24), y, w: p.span(342), h: rowH - 3, action: () => { this.config.answerMode = mode; } });
    });
    const publicY = answerY + 3 * rowH + 51;
    this.ruleLine(25, publicY - 19, 340); p.text('公开房间', p.x(25), publicY + 3, T.body, C.ink, '600');
    p.box(p.x(312), publicY - 9, 50, 27, this.config.isPublic ? C.accentDeep : C.line, 14); p.circle(p.x(312)+(this.config.isPublic?36:14), publicY + 4.5, 10, C.white);
    p.hits.push({ id: 'live-public', label: this.config.isPublic ? '设为非公开房间' : '设为公开房间', x: p.x(303), y: publicY - 15, w: 63, h: 38, action: () => { this.config.isPublic = !this.config.isPublic; } });
    if (publicY + 49 < h - 80) p.text(this.config.isPublic ? '大厅可见' : '仅凭房间码加入', p.x(25), publicY + 28, T.note, C.mute);
    this.button(editing ? 'settings-save' : 'create-submit', editing ? '保存设置' : '开启派对', 24, h - 65, 342, 45, () => void this.task(() => editing ? this.saveSettings() : this.create()), true, this.loading || (editing && !this.connected), editing ? 'check' : 'plus');
  }
  private renderTools() {
    renderDrawingTools(this.host, this, this.board.toolsY, this.board.toolsHeight ?? 150, this.board.size, {sliding:this.slidingId!==null,
      disabled: !this.canDraw(), canUndo: this.model.canUndo, canRedo: this.model.canRedo, background: this.model.drawing.background,
      cancelLine: () => this.cancelLine(),
      palette: () => { this.host.studio.openShop('palette'); },
      undo: () => this.operation({ kind: 'undo' }), redo: () => this.operation({ kind: 'redo' }),
      clear: () => { const roundId = this.room?.round?.id; void this.task(async () => {
        if (await this.host.platform.confirm('换一张空白画纸？', '清空后仍可通过撤销找回上一幅画。') && this.canDraw() && this.room?.round?.id === roundId && (this.model.drawing.strokes.length || this.model.drawing.background)) this.operation({ kind: 'clear' });
      }); },
      fill: () => { this.operation({ kind: 'fill', background: this.color }); this.eraser = false; this.host.toast('背景已经换成这个颜色，撤销可以还原。'); },
    }, 'live-');
  }
  private renderGuess(now: number) {
    const p = this.host.paint; const room = this.room!; const round = room.round!; const b = this.board; const y = b.toolsY;
    if (room.role === 'spectator' || this.me()?.guessed) {
      const solved = this.me()?.guessed; const color = solved ? '#327E66' : C.accentDeep;
      p.stamp(p.x(27), y + 8, 53, 58); p.icon(solved ? 'check' : 'frame', p.x(53), y + 37, 25, color);
      p.text(solved ? '这一笔，你懂了' : '先看这一题', p.x(99), y + 24, T.section, C.ink, '700');
      p.fit(solved ? now < this.answerFeedbackUntil ? this.answerFeedback : '已猜中 · 等待其他友友' : '下一题，和大家一起猜', p.x(99), y + 51, p.span(261), T.caption, color);
      const pending = room.members.filter(member => !member.spectator && member.id !== round.artistId && !member.guessed);
      if (y + 116 < b.seatsY - 8) {
        this.ruleLine(26, y + 84, 338);
        p.fit(pending.length ? `${pending.map(member => member.nickname).join('、')} 还在思考` : '大家的答案都到齐了', p.x(26), y + 108, p.span(338), T.caption, C.mute);
      }
      return;
    }
    const banks = round.banks ?? []; const hasCandidates = room.answerMode !== 'text' && banks.length > 0;
    const hasText = room.answerMode !== 'candidates' || (round.letterCount ?? 0) === 1;
    if (this.keyboardOpen) {
      p.box(p.x(24), y, p.span(266), 38, C.white, 8, C.line); p.fit(this.answer || '写下你的答案', p.x(39), y + 19, p.span(236), T.body, this.answer ? C.ink : C.mute);
      p.iconButton('live-send-text', '提交答案', 'send', p.x(333), y + 19, 19, () => void this.task(() => this.guess(this.answer)), 'orange', !this.answer.trim() || !this.canGuess());
      return;
    }
    if (hasCandidates) {
      const count = banks.length; const stage = Math.min(this.selected.length, count - 1); const characters = this.selected.map((index, i) => banks[i]?.[index] ?? '');
      const slotW = Math.min(43, 278 / count); const start = this.host.x(171) - count * slotW / 2;
      for (let i = 0; i < count; i++) {
        const x = start + i * slotW; const active = i === this.selected.length; const filled = i < characters.length;
        const pop = filled && i === characters.length - 1 && now - this.pickedAt < 160 ? 1 + 0.08 * Math.sin((now - this.pickedAt) / 160 * Math.PI) : 1;
        p.ctx.save(); p.ctx.translate(x + slotW / 2, y + b.slotH / 2); p.ctx.scale(pop, pop); p.ctx.translate(-(x + slotW / 2), -(y + b.slotH / 2));
        p.box(x + 2, y, slotW - 4, b.slotH, filled ? C.tint : C.white, 8, active ? C.orange : C.line, filled ? 0 : 4);
        if (filled) p.text(characters[i], x + slotW / 2, y + b.slotH / 2, T.glyph, C.accentDeep, '700', 'center');
        else {
          p.dashed(x + 9, y + b.slotH - 8, x + slotW - 9, y + b.slotH - 8, C.line, 1, [2, 3]);
          if (active && Math.floor(now / 550) % 2 === 0) p.line([[x + slotW / 2, y + 9], [x + slotW / 2, y + b.slotH - 12]], C.orange, 1.5);
        }
        p.ctx.restore();
      }
      this.icon('answer-clear', '重新选字', 'undo', 343, y + b.slotH / 2, () => { this.selected.pop(); }, !this.selected.length);
      const feedback = this.answerFeedback && now < this.answerFeedbackUntil;
      const label = feedback ? this.answerFeedback : this.selected.length === count ? '答案拼好了' : `第 ${this.selected.length + 1} / ${count} 个字`;
      p.text(label, p.x(26), y + b.slotH + 17, T.note, feedback ? C.orangeDeep : C.mute);
      for (let i = 0; i < count; i++) p.circle(349 - (count - 1 - i) * 11, y + b.slotH + 17, 2.5, i < this.selected.length ? C.accent : C.line);
      const tilesY = y + b.slotH + 29; const cell = (this.host.W - 50) / 7; const bank = banks[stage];
      bank.forEach((character, index) => {
        const x = 25 + index % 7 * cell; const ty = tilesY + Math.floor(index / 7) * (b.tileH + 6);
        const disabled = !character || !this.canGuess() || this.selected.length >= count;
        const pressed = p.pressedId === `live-candidate-${stage}-${index}`;
        p.box(x, ty + (pressed ? 1 : 0), cell - 6, b.tileH, disabled ? C.paper : pressed ? C.tint : C.cream, 8, C.line, disabled ? 0 : 5);
        p.text(character, x + (cell - 6) / 2, ty + b.tileH / 2, T.glyph, disabled ? C.mute : C.ink, '700', 'center');
        p.hits.push({ id: `live-candidate-${stage}-${index}`, label: character, x, y: ty, w: cell - 6, h: b.tileH, disabled, action: () => { if (this.selected.length < count) { this.selected.push(index); this.pickedAt = Date.now(); this.answerFeedbackUntil = 0; } } });
      });
      const controlsY = tilesY + b.tileH * 2 + 16;
      if (hasText) this.button('answer-input', '自己输入', 24, controlsY, 164, b.controlH, () => void this.task(() => this.keyboard()), false, !this.canGuess(), 'brush');
      this.button('guess-submit', '就是这个', hasText ? 202 : 24, controlsY, hasText ? 164 : 342, b.controlH, () => void this.task(() => this.guess()), true, this.selected.length !== count || !this.canGuess() || this.queuedGuess.size > 0, 'send');
    } else {
      p.text((round.letterCount ?? 0) === 1 ? '单字题 · 自己写下答案' : `${round.letterCount} 个字 · 你猜是什么`, p.x(26), y + 17, T.caption, C.mute);
      p.box(p.x(24), y + 37, p.span(342), 52, C.white, 8, C.line, 5); p.icon('brush', p.x(47), y + 63, 17, C.accentDeep);
      p.fit(this.answer || '写下你的答案', p.x(72), y + 63, p.span(246), T.section, this.answer ? C.ink : C.mute, this.answer ? '600' : '400');
      p.icon('send', p.x(342), y + 63, 15, C.accentDeep);
      p.hits.push({ id: 'live-answer-input', label: '输入你的答案', x: p.x(24), y: y + 37, w: p.span(342), h: 52, action: () => void this.task(() => this.keyboard()), disabled: !this.canGuess() });
      if (this.answerFeedback && now < this.answerFeedbackUntil) p.text(this.answerFeedback, p.x(195), y + 116, T.caption, C.orangeDeep, '500', 'center');
    }
  }
  private renderReveal(h: number) {
    const p = this.host.paint; const round = this.room!.round!; const y = this.board.toolsY;
    p.text(round.word ?? '本题结束', p.x(195), y + 12, T.title, C.accentDeep, '700', 'center');
    const result = round.results?.find(item => item.userId === this.host.player?.id);
    p.icon('star', p.x(55), y + 48, 15, C.gold);
    p.fit(result ? `本题 +${result.score} 分 · 星星整局结算` : round.endReason ?? '一起欣赏这张画', p.x(76), y + 48, p.span(283), T.bodyStrong, C.ink, '600');
    this.ruleLine(25, y + 70, 340);
    const rank = [...this.room!.members].sort((a, b) => b.score - a.score);
    const rows = Math.max(1, Math.min(3, Math.floor((h - 55 - y - 79) / 25)));
    rank.slice(0, rows).forEach((member, index) => {
      const row = y + 92 + index * 25;
      p.text(String(index + 1).padStart(2, '0'), p.x(32), row, T.note, index === 0 ? C.orangeDeep : C.mute, '600');
      p.fit(member.nickname, p.x(67), row, p.span(208), T.sub, member.id === this.host.player?.id ? C.accentDeep : C.ink);
      p.text(`${member.score} 分`, p.x(355), row, T.sub, C.ink, '600', 'right');
    });
  }
  private renderDetailPanel() {
    const p = this.host.paint; const h = this.host.H - this.host.top;
    const settlement = this.panel === 'settlement'; const height = settlement ? Math.min(560, h - 88) : 220; const y = Math.max(12, (h - height - 55) / 2);
    p.hits = []; p.ctx.save(); p.ctx.fillStyle = 'rgba(46,53,66,.25)'; if(!this.host.tablet)p.ctx.fillRect(0, 0, p.width, h); p.ctx.restore();
    p.card(p.x(24), y, p.span(342), height, 18); p.tape(p.x(55), y + 1, -.2, 35, 10);
    this.icon('panel-close', '返回房间', 'undo', 342, y + 25, () => { this.panel = null; });
    if (!settlement) {
      p.text('友友的答案', p.x(42), y + 26, T.section, C.ink, '700');
      const letters = [...this.bubbleText]; for (let i = 0; i < letters.length; i += 20) p.text(letters.slice(i, i + 20).join(''), p.x(42), y + 62 + Math.floor(i / 20) * 24, T.body, C.ink);
      return;
    }
    const result = this.room?.lastMatch; if (!result) return;
    this.seatPositions.clear();
    p.text(result.completed ? '这一局，默契满分' : '本局已中止', p.x(42), y + 26, T.section, C.accentDeep, '700');
    p.text(result.completed ? '星星已到账 · 等待房主开启新一局' : result.reason + ' · 不发放星星', p.x(42), y + 56, T.caption, C.mute);
    p.text('名次 / 友友', p.x(42), y + 87, T.note, C.mute); p.text('积分   应得 / 到账', p.x(344), y + 87, T.note, C.mute, '400', 'right');
    const pageSize = Math.max(1, Math.min(10, Math.floor((height - 150) / 31))); const pages = Math.max(1, Math.ceil(result.results.length / pageSize)); this.panelPage = Math.min(this.panelPage, pages - 1);
    result.results.slice(this.panelPage * pageSize, (this.panelPage + 1) * pageSize).forEach((r, i) => {
      const cy = y + 111 + i * 31; p.text(r.rank ? String(r.rank) : '—', p.x(43), cy, T.sub, r.rank === 1 ? C.orangeDeep : C.mute, '700');
      const member = this.room!.members.find(m => m.id === r.userId);
      if (member) {
        this.avatar(member, p.x(77), cy, 11); this.seatPositions.set(member.id, { x: p.x(77), y: cy });
        p.hits.push({ id: 'live-result-target-' + member.id, label: '向' + member.nickname + '发送互动', x: p.x(62), y: cy - 14, w: 30, h: 28, action: () => { this.reactionTarget = member.id; } });
      }
      p.fit(r.nickname, p.x(96), cy, p.span(115), T.sub, r.userId === this.host.player?.id ? C.accentDeep : C.ink);
      p.text(`${r.score}`, p.x(248), cy, T.sub, C.ink, '600', 'right');
      p.text(r.eligible ? `${r.expectedStars} / ${r.stars} 星` : '无奖励资格', p.x(345), cy, T.note, r.stars ? C.orangeDeep : C.mute, '400', 'right');
    });
    const footer = y + height - 27;
    if (pages > 1) { this.button('results-prev', '上一页', 39, footer - 12, 76, 25, () => { this.panelPage--; }, false, this.panelPage === 0); this.button('results-next', '下一页', 275, footer - 12, 76, 25, () => { this.panelPage++; }, false, this.panelPage === pages - 1); }
    p.text(`今日还可获得 ${this.room!.dailyStarsRemaining} 星`, p.x(195), footer, T.note, C.mute, '400', 'center');
  }
  private renderPanel() {
    const p=this.host.paint;
    if(!this.host.tablet || this.panel==='brush'){this.renderPanelContents();return;}
    const width=p.width,left=(width-Math.min(width,640))/2;
    p.box(0,-this.host.top,width,this.host.H,'rgba(46,53,66,.22)',0);
    p.ctx.save();p.ctx.translate(left,0);p.width=Math.min(width,640);
    try { this.renderPanelContents(); }
    finally {
      p.hits=p.hits.map(hit=>hit.id==='live-panel-dismiss'?{...hit,x:0,w:width}:{...hit,x:hit.x+left});
      if(this.panel==='settlement')for(const [id,point] of this.seatPositions)this.seatPositions.set(id,{...point,x:point.x+left});
      p.width=width;p.ctx.restore();
    }
  }
  private renderPanelContents() {
    const p = this.host.paint; const h = this.host.H - this.host.top; const panel = this.panel;
    const roster = panel === 'ranking' || panel === 'members';
    if (panel === 'brush') {
      const box = brushCatalogBox(this.board.y, this.board.toolsY);
      p.hits.push({ id: 'live-panel-surface', label: '笔头面板', x: p.x(24), y: box.y, w: p.span(342), h: box.height, action: () => { this.panel = 'brush'; } });
      renderBrushCatalog(this.host, this, box, this.board.size, brush => selectDrawingBrush(this.host, this, brush, () => this.cancelLine()), 'live-');
      return;
    }
    if (panel === 'settlement' || panel === 'bubble') { this.renderDetailPanel(); return; }
    const height = roster ? Math.min(530, h - 106) : 207;
    const y = Math.max(85, (h - height) / 2);
    p.hits = [];
    p.hits.push({ id: 'live-panel-dismiss', label: '关闭面板', x: 0, y: 0, w: p.width, h, action: () => { this.panel = null; } });
    { p.ctx.save(); p.ctx.fillStyle = 'rgba(46,53,66,0.22)'; if(!this.host.tablet)p.ctx.fillRect(0, 0, p.width, h); p.ctx.restore(); }
    p.card(p.x(24), y, p.span(342), height, 18); p.tape(p.x(51), y + 1, -0.25, 35, 10);
    p.hits.push({ id: 'live-panel-surface', label: '面板', x: p.x(24), y, w: p.span(342), h: height, action: () => { this.panel = panel; } });
    p.text(roster ? '这一桌的友友' : '给友友一点回应', p.x(41), y + 25, T.section, C.ink, '700');
    this.icon('panel-close', '关闭面板', 'undo', 341, y + 25, () => { this.panel = null; });
    if (roster) {
      for (const [index, mode] of (['members', 'ranking'] as const).entries()) {
        const x = p.x(40 + index * 152); const active = panel === mode;
        if (active) p.box(x, y + 48, p.span(145), 30, C.tint, 6);
        p.text(mode === 'members' ? '成员' : '积分', x + p.span(72), y + 63, T.sub, active ? C.accentDeep : C.mute, active ? '700' : '400', 'center');
        p.hits.push({ id: 'live-roster-' + mode, label: mode === 'members' ? '查看成员' : '查看积分', x, y: y + 48, w: p.span(145), h: 30, action: () => { this.panel = mode; } });
      }
      const members = [...this.room!.members]; if (panel === 'ranking') members.sort((a, b) => b.score - a.score);
      members.forEach((member, index) => {
        const rowHeight = (height - 125) / Math.max(1, members.length); const cy = y + 94 + index * rowHeight;
        this.avatar(member, p.x(58), cy, 13);
        p.fit(member.nickname, p.x(88), cy - 7, p.span(177), T.sub, member.id === this.host.player?.id ? C.accentDeep : C.ink, '600');
        p.text(this.memberStatus(member), p.x(88), cy + 11, T.tiny, member.guessed ? '#327E66' : C.mute);
        if (panel === 'ranking') p.text(String(member.score), p.x(341), cy, T.bodyStrong, C.accentDeep, '700', 'right');
        else if (member.id === this.room!.ownerId) { p.star(p.x(326), cy, 5, C.gold); p.text('房主', p.x(346), cy + 16, T.tiny, C.mute, '400', 'right'); }
        if (index < members.length - 1) p.line([[p.x(88), cy + 17], [p.x(341), cy + 17]], C.line, 0.6);
      });
      this.ruleLine(41, y + height - 42, 307); p.text(panel === 'ranking' ? `今日还可获得 ${this.room!.dailyStarsRemaining} 星` : `${members.length} 位友友 · ${this.room!.isPublic ? '公开房间' : '邀请房间'}`, p.x(195), y + height - 21, T.caption, C.mute, '400', 'center');
    } else FEEDBACK.forEach((text, index) => {
      const x = 39 + index % 2 * 163; const cy = y + 61 + Math.floor(index / 2) * 44;
      this.button('feedback-' + index, text, x, cy, 149, 33, () => void this.task(() => this.feedback(index)), false, Date.now() - this.lastFeedback < 3000);
    });
  }
}
